import React, { useRef, useEffect } from 'react';
import { World } from '@/data/worlds';

interface PostcardGeneratorProps {
  world: World;
  task: string;
  duration: number;
  onGenerate?: (blob: Blob) => void;
}

const PostcardGenerator: React.FC<PostcardGeneratorProps> = ({
  world,
  task,
  duration,
  onGenerate,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size (16:9 aspect ratio)
    const width = 1200;
    const height = 675;
    canvas.width = width;
    canvas.height = height;

    // Draw background gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, world.colorScheme.secondary);
    gradient.addColorStop(1, world.colorScheme.background);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Add subtle radial gradient overlay
    const radialGradient = ctx.createRadialGradient(
      width * 0.3,
      height * 0.3,
      0,
      width * 0.3,
      height * 0.3,
      width * 0.6
    );
    radialGradient.addColorStop(0, `${world.colorScheme.primary}40`);
    radialGradient.addColorStop(1, 'transparent');
    ctx.fillStyle = radialGradient;
    ctx.fillRect(0, 0, width, height);

    // Bottom gradient overlay for text readability
    const bottomGradient = ctx.createLinearGradient(0, height * 0.5, 0, height);
    bottomGradient.addColorStop(0, 'transparent');
    bottomGradient.addColorStop(1, 'rgba(0, 0, 0, 0.7)');
    ctx.fillStyle = bottomGradient;
    ctx.fillRect(0, 0, width, height);

    // Draw duration
    ctx.font = 'bold 120px "Space Grotesk", system-ui, sans-serif';
    ctx.fillStyle = 'white';
    ctx.textAlign = 'center';
    ctx.fillText(`${duration}`, width / 2, height * 0.4);

    // Draw "minutes focused"
    ctx.font = '24px "Space Grotesk", system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.fillText('minutes focused', width / 2, height * 0.4 + 50);

    // Draw task (truncated if too long)
    const displayTask = task.length > 50 ? task.substring(0, 47) + '...' : task;
    ctx.font = 'italic 28px "Playfair Display", Georgia, serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillText(`"${displayTask}"`, width / 2, height * 0.65);

    // Draw world name
    ctx.font = '18px "Space Grotesk", system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.textAlign = 'left';
    ctx.fillText(world.name, 40, height - 40);

    // Draw branding
    ctx.textAlign = 'right';
    ctx.fillText('kaizenflow.co', width - 40, height - 40);

    // Generate blob for download/share
    canvas.toBlob((blob) => {
      if (blob && onGenerate) {
        onGenerate(blob);
      }
    }, 'image/png');
  }, [world, task, duration, onGenerate]);

  return (
    <div className="postcard glass rounded-xl overflow-hidden">
      {/* Preview using canvas */}
      <canvas
        ref={canvasRef}
        className="w-full h-auto"
        style={{ display: 'block' }}
      />
    </div>
  );
};

export default PostcardGenerator;
